import React, { Component, createContext, useContext } from 'react';
import { Auth0Context } from '@auth0/auth0-react';
import { connectorsService } from '../services/api/connectors.service';
import { settingsService } from '../services/api/settings.service';
import { datasetsService } from '../services/api/datasets.service';
import { accountService } from '../services/api/account.service';
import { LOCAL_APPS } from '../constants/Config';

// create the context
export const DataContext = createContext({});
export const useData = () => useContext(DataContext);

class DataProvider extends Component {
  static contextType = Auth0Context;

  state = {
    isApiLoading: true,
    connectors: null,
    datasets: null,
    default_dataset: null,
    subdomain: null,
    statsSubdomain: null,
    is_customer: false,
    init_status: null,
    payment_link: null,
    hc_analytics_enabled: false,
    hc_events_key: null,
    articles_count: null,
    analytics_account: null,
    translate_account: null,
    product_account: null,
    has_translate: false,
    product: null,
    active_products: null,
    trial_usage: null,
    trial_info: null,
  };

  componentDidMount() {
    this.initData();
  }

  calculateDaysToExpire(trial_started_at, trial_length) {
    if (!trial_started_at) {
      return 0;
    }
    trial_length = trial_length >= 0 ? trial_length : 14;
    const daysSinceStart = (new Date().getTime() - new Date(trial_started_at).getTime()) / (1000 * 3600 * 24);
    return Math.max(Math.ceil(trial_length - daysSinceStart), 0);
  }

  // initialize the auth0 library
  initData = async () => {
    this.setState({ isApiLoading: true });
    const auth0 = this.context;
    let token = null;
    try {
      token = await auth0.getAccessTokenSilently();
    } catch (err) {
      console.log('Got an error', err);
      this.setState({ isApiLoading: false });
      return auth0.loginWithRedirect();
    }

    let product = this.props.product || localStorage.getItem('current_product');
    if (LOCAL_APPS.indexOf(product) === -1) {
      product = null;
    } else if (this.props.product) {
      localStorage.setItem('current_product', product);
    }

    let active_products;
    try {
      active_products = await settingsService.getSetting(token, 'product');
      if (typeof active_products === 'string') {
        active_products = [active_products];
      }
      if (!product) {
        product = (active_products || []).find((p) => LOCAL_APPS.indexOf(p) >= 0);
        if (!product) {
          product = 'hcmanager';
        }
      }
    } catch (err) {
      product = localStorage.getItem('swifteq_product');
      if (product) {
        try {
          await settingsService.updateSetting(token, 'product', [product]);
        } catch (err) {
          console.error(err);
        }
      }
    }

    try {
      const [connectors, account_info, datasets] = await Promise.all([
        connectorsService.getConnectors(token, product),
        accountService.getInfo(token, product),
        datasetsService.getDatasets(token, product),
      ]);

      let default_dataset = datasets.find((d) => d.name === account_info.default_dataset || 'Zendesk Support');
      if (!default_dataset) {
        default_dataset = datasets[0];
      }

      const trial_info = { status: 'done', tasks: {} };
      if (account_info.product_account) {
        trial_info.status = account_info.product_account.trial_status || 'done';
        trial_info.started_at = account_info.product_account.trial_started_at || undefined;
        if (trial_info.started_at) {
          trial_info.days_to_expire = this.calculateDaysToExpire(trial_info.started_at, account_info.product_account.trial_length);
        }
      }
      if (account_info.analytics_account) {
        trial_info.analytics = {
          started_at: account_info.analytics_account.trial_started_at,
          days_to_expire: this.calculateDaysToExpire(account_info.analytics_account.trial_started_at, account_info.analytics_account.trial_length),
        };
      }

      let subdomain = null;
      let statsSubdomain = null;
      if (default_dataset && (default_dataset.subdomains || []).length > 0) {
        const default_sub = default_dataset.subdomains.find((s) => s.default);
        subdomain = default_sub ? default_sub.value : default_dataset.subdomains[0].value;
        statsSubdomain = default_sub ? default_sub.stats : default_dataset.subdomains.find((sd) => sd.value === subdomain)?.stats;
      }

      this.setState({
        connectors,
        datasets,
        default_dataset,
        subdomain,
        statsSubdomain,
        is_customer: account_info.is_customer,
        has_translate: account_info.has_translate,
        init_status: account_info.init_status === 'expired' || connectors.some((c) => c.authenticated)
          ? account_info.init_status
          : 'registered',
        payment_link: account_info.payment_link,
        hc_analytics_enabled: account_info.hc_analytics_enabled,
        hc_events_key: account_info.hc_events_key,
        articles_count: account_info.articles_count || 0,
        analytics_account: account_info.analytics_account,
        translate_account: account_info.translate_account,
        product_account: account_info.product_account,
        product,
        active_products,
        trial_usage: account_info.trial_usage || null,
        trial_info,
        isApiLoading: false,
      });
    } catch (err) {
      console.log('Got an error', err);
      this.setState({ isApiLoading: false });
    }
  };

  setDefaultDataset = (new_dataset) => {
    this.setState({
      default_dataset: new_dataset,
    });
  };

  setSubdomain = (new_subdomain) => {
    const { default_dataset } = this.state;
    let statsSubdomain = null;
    if (default_dataset && default_dataset.subdomains) {
      const selectedSubdomain = default_dataset.subdomains.find((sd) => sd.value === new_subdomain);
      if (selectedSubdomain) {
        statsSubdomain = selectedSubdomain.stats;
      }
    }
    this.setState({
      subdomain: new_subdomain,
      statsSubdomain,
    });
  };

  render() {
    const {
      isApiLoading,
      connectors,
      datasets,
      default_dataset,
      subdomain,
      statsSubdomain,
      is_customer,
      has_translate,
      init_status,
      payment_link,
      hc_analytics_enabled,
      hc_events_key,
      articles_count,
      analytics_account,
      translate_account,
      product_account,
      product,
      active_products,
      trial_usage,
      trial_info,
    } = this.state;
    const { children } = this.props;

    const configObject = {
      isApiLoading,
      connectors,
      datasets,
      default_dataset,
      subdomain,
      statsSubdomain,
      is_customer,
      has_translate,
      init_status,
      payment_link,
      hc_analytics_enabled,
      hc_events_key,
      articles_count,
      analytics_account,
      translate_account,
      product_account,
      product,
      active_products,
      trial_usage,
      trial_info,
      setDefaultDataset: (new_dataset) => this.setDefaultDataset(new_dataset),
      setSubdomain: (new_subdomain) => this.setSubdomain(new_subdomain),
    };

    return <DataContext.Provider value={configObject}>{children}</DataContext.Provider>;
  }
}

export default DataProvider;
